import { useEffect, useState } from 'react';
import '../css/bg.css';
import Background from '../Components/background/background';
import Logo from "../Components/Assets/logo.svg";
import segLogo from "../Components/Assets/seg-logo.png";
import HeroImage from "../Components/Assets/hero.jpg";
import { useNavigate } from "react-router-dom";
import config from '../Context/Config';
import { checkTokenAndNavigate } from "../Utils/login";

const LoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    // Validate configuration before proceeding
    if (!validateConfig()) {
      setConfigError("System configuration error. Please contact support.");
      return;
    }
    
    checkTokenAndNavigate(navigate);
  }, [navigate]);

  const validateConfig = () => {
    const requiredFields = ['AUTH_URL', 'CLIENT_ID', 'REDIRECT_URI'];
    
    for (const field of requiredFields) {
      if (!config[field] || typeof config[field] !== 'string' || config[field].trim() === '') {
        console.error(`Missing or invalid config: ${field}`);
        return false;
      }
    }

    // Validate URL format
    try {
      new URL(config.AUTH_URL);
      new URL(config.REDIRECT_URI);
    } catch (error) {
      console.error('Invalid URL in configuration');
      return false;
    }

    return true;
  };

  const loginWindow = async () => {
    if (!validateConfig()) {
      setConfigError("Configuration error. Please refresh the page and try again.");
      return;
    }

    try {
      setIsLoading(true);
      
      // Properly encode URL parameters
      const params = new URLSearchParams({
        client_id: config.CLIENT_ID.trim(),
        response_type: 'code',
        scope: 'openid profile email',
        redirect_uri: config.REDIRECT_URI.trim(),
        kc_idp_hint: 'microsoft'
      });

      // Construct secure URL
      const authUrl = `${config.AUTH_URL.trim()}?${params.toString()}`;
      
      // Add small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Redirect to authentication
      window.location.href = authUrl;
      
    } catch (error) {
      console.error('Login initialization error:', error);
      setIsLoading(false);
      setConfigError("Unable to initialize login. Please try again.");
    }
  };

  const LogoGroup = ({ logos }) => (
    <div className="flex items-center gap-4 mb-8">
      {logos.map((logo, index) => (
        <img
          key={index}
          src={logo.src}
          alt={logo.alt || `Logo ${index + 1}`}
          loading="eager"
          className="w-50 h-28 rounded-lg bg-white p-2"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ))}
    </div>
  );

  const logos = [
    { src: Logo, alt: "Main Logo" },
    { src: segLogo, alt: "Secondary Logo" },
  ];

  // Handle configuration errors with simple styling
  if (configError) {
    return (
      <div className="relative h-screen w-screen bg-white-200 overflow-hidden">
        <Background />
        <div className="relative flex items-center justify-center h-screen z-10">
          <div className="bg-white shadow-lg rounded-lg p-8 max-w-md text-center">
            <h1 className="text-xl font-bold text-red-600 mb-4">Service Unavailable</h1>
            <p className="text-gray-600 mb-4">{configError}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      {/* Background Section: Waves */}
      <Background />

      {/* Accent elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -mr-48 -mt-48"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl -ml-48 -mb-48"></div>

      {/* Content Section */}
      <div className="relative flex flex-col md:flex-row items-center justify-center h-screen z-10">
        {/* Left Section: Image */}
        <div
          className="relative w-full md:w-3/4 h-3/4 max-w-lg bg-cover bg-no-repeat bg-center rounded-xl overflow-hidden mb-8 md:mb-0 md:ml-8 z-10"
          style={{ backgroundImage: `url(${HeroImage})` }}
        >
          <div className="absolute inset-0 bg-white opacity-10 rounded-xl"></div>
        </div>

        {/* Right Section: Content */}
        <div className="flex flex-col items-center justify-center text-center md:w-1/2 px-6 md:px-8 py-8 z-10">
          <LogoGroup logos={logos} />

          <h1 className="text-4xl md:text-5xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-cyan-300 to-blue-400">
            Digital Forms
          </h1>

          <p className="text-sm md:text-base text-blue-200/70 mb-8 font-medium tracking-wide">
            Streamlined Form Management
          </p>

          <div className="w-full max-w-sm mb-8">
            <button
              onClick={loginWindow}
              disabled={isLoading}
              className={`
                w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-3 px-6 rounded-xl
                shadow-xl hover:shadow-2xl hover:from-blue-600 hover:to-cyan-600
                focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900
                transition-all duration-300 ease-in-out text-base font-semibold
                flex items-center justify-center gap-3
                ${isLoading ? 'opacity-80 cursor-not-allowed' : 'hover:scale-105'}
              `}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <i className="fab fa-microsoft text-lg"></i>
                  <span>Sign In with Microsoft</span>
                </>
              )}
            </button>
          </div>

          <div className="text-blue-200/60 text-sm space-y-1">
            <p className="font-medium">Powered By</p>
            <p className="text-blue-300 font-semibold">Clear Healthcare</p>
          </div>

          {/* Environment Indicator */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 px-4 py-2 bg-amber-500/20 border border-amber-400/50 text-amber-300 rounded-lg text-xs font-medium backdrop-blur-sm">
              Development Environment
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;